import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Paper,
    Box,
    Grid,
    Switch,
    FormControlLabel,
    Alert,
} from '@mui/material';
import {
    Add as AddIcon,
    PeopleOutline as PeopleOutlineIcon,
    PersonOutline as PersonOutlineIcon,
} from '@mui/icons-material';
import {
    AppContext,
    SttButton,
    SttList,
    SttSelectMshipDialog,
    SelectGroupPopup,
} from '../../../sporttia/all';

type Props = {
    receptors: any[];
    sendAllSC: boolean;
    setSendAllSC: (value: boolean) => void;
    onAddReceptor: (receptor: any) => void;
    onDelReceptor: (receptor: any) => void;
};

export default function Receptors({
    receptors,
    sendAllSC,
    setSendAllSC,
    onAddReceptor,
    onDelReceptor,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [openedSelectingMship, setOpenedSelectingMship] =
        useState<boolean>(false);
    const [openedSelectingGroup, setOpenedSelectingGroup] =
        useState<boolean>(false);

    const addReceptor = ({ mship, group }: { mship?: any; group?: any }) => {
        // Add mship to receptors
        if (mship) {
            // Check if it exists
            if (
                receptors.find((e: any) => e.mship && e.mship.id === mship.id)
            ) {
                cxt?.showMessage('E', t('UserAlreadyOnList'));
            } else {
                onAddReceptor({
                    id: receptors.length,
                    caption: mship.name,
                    mship,
                    objToSelect: mship,
                    avatar: <PersonOutlineIcon />,
                });
                cxt?.showMessage('S', t('generic.added'));
            }
        }

        // Add group to receptors
        if (group) {
            // Check if it exists
            if (
                receptors.find((e: any) => e.group && e.group.id === group.id)
            ) {
                cxt?.showMessage('E', t('GroupAlreadyExists'));
            } else {
                onAddReceptor({
                    id: receptors.length,
                    caption: group.name,
                    group,
                    objToSelect: group,
                    avatar: <PeopleOutlineIcon />,
                });
                cxt?.showMessage('S', t('generic.added'));
            }
        }
    };

    return (
        <Paper>
            <Box p={2}>
                <FormControlLabel
                    label={t('SendAllSC')}
                    control={
                        <Switch
                            checked={sendAllSC}
                            onChange={(ev: any) =>
                                setSendAllSC(ev.target.checked)
                            }
                            color="primary"
                        />
                    }
                />

                {sendAllSC ? (
                    <Box mt={2}>
                        <Alert severity="info">{t('SendAllSCAlert')}</Alert>
                    </Box>
                ) : (
                    <>
                        <SttList
                            title={t('Receptors')}
                            numRows={8}
                            data={receptors}
                            onDelete={onDelReceptor}
                        />

                        <Box mt={2}>
                            <Grid container spacing={1}>
                                <Grid size={{ xs: 6 }}>
                                    <SttButton
                                        fullWidth
                                        startIcon={<AddIcon />}
                                        caption={t('generic.user')}
                                        onClick={() =>
                                            setOpenedSelectingMship(true)
                                        }
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <SttButton
                                        fullWidth
                                        startIcon={<AddIcon />}
                                        caption={t('generic.group')}
                                        onClick={() =>
                                            setOpenedSelectingGroup(true)
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                )}
            </Box>

            <SttSelectMshipDialog
                open={openedSelectingMship}
                onClose={() => setOpenedSelectingMship(false)}
                onSelectMship={(mship: any) => addReceptor({ mship })}
            />

            {openedSelectingGroup && (
                <SelectGroupPopup
                    open
                    onClose={() => setOpenedSelectingGroup(false)}
                    onSelect={(group: any) => addReceptor({ group })}
                />
            )}
        </Paper>
    );
}
